import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { ProjectService } from '@/services/ProjectService';
import ProjectHeader from '@/components/header';
import {
    GripVertical,
    X,
    ChevronDown,
    Eye,
    EyeOff,
    CopyPlus,
    Plus,
    MoveDiagonal2,
    Hand,
    Info,
    Filter,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AssetsConfig } from '@/services/AssetsConfig';
import CustomLayerDialog from '@/components/CustomLayerDialog';
import { ReactComponent as Custom } from '@/components/icons/custom_mood_2.svg';
import DuplicateLayerDialog from '@/components/moodboardComponents/DuplicateLayerDialog';
import CollectionNavigation from '@/components/CollectionNavigation';
import { useCollectionData } from '@/hooks/useCollectionData';

const UserMoodboardScreen = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const [groupedData, setGroupedData] = useState({});
    const [selectedItems, setSelectedItems] = useState({});
    const [isDropdownOpen, setIsDropdownOpen] = useState({});
    const [loading, setLoading] = useState(true);
    const [categoryFilters, setCategoryFilters] = useState({});
    const [availableCategories, setAvailableCategories] = useState({});
    const [selectedLayerId, setSelectedLayerId] = useState(null);
    const [draggedLayer, setDraggedLayer] = useState(null);
    const [dragOverInfo, setDragOverInfo] = useState(null);
    const [isLayerControlsOpen, setIsLayerControlsOpen] = useState(true);
    const [layerPanelDropdowns, setLayerPanelDropdowns] = useState({});
    // const [isMaterialsOpen, setIsMaterialsOpen] = useState(false);
    const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
    const [layerToDuplicate, setLayerToDuplicate] = useState(null);
    const [showInstructions, setShowInstructions] = useState(false);
    const canvasWidth = 800;
    const canvasHeight = 600;
    const thumbnailSize = 200;
    const canvasOffset = 50;

    // Canvas-related state
    const [layers, setLayers] = useState([]);
    const [draggingLayer, setDraggingLayer] = useState(null);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const canvasRef = useRef(null);
    const layerImagesRef = useRef({});
    // Add new state for resize handling
    const [isResizing, setIsResizing] = useState(false);
    const [initialSize, setInitialSize] = useState({ width: 0, height: 0 });
    const [initialMouse, setInitialMouse] = useState({ x: 0, y: 0 });
    const [resizeStartScale, setResizeStartScale] = useState(1);
    const [isHandleHovered, setIsHandleHovered] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const saveTimeoutRef = useRef(null);
    // const [materialsData, setMaterialsData] = useState([]);
    const [refreshedUrls, setRefreshedUrls] = useState({});
    const [fileItems, setFileItems] = useState([]);
    const [projectData, setProjectData] = useState(null);
    const { data: collectionData, iscollectionLoading } = useCollectionData(projectData?.collectionId);
    const [businessId, setBusinessId] = useState(null);

    const FALLBACK_IMAGE = 'https://gni-public-bucket.s3.us-east-1.amazonaws.com/assets/gni/notAvailible.jpg';

    const isCustomLayer = type => type.startsWith('Custom_');

    useEffect(() => {
        const initializeData = async () => {
            try {
                // Get the project first
                const currentProject = await ProjectService.getProjectById(projectId);
                setProjectData(currentProject);
                setFileItems(currentProject.fileItems || []);
                // console.log('fileItems:', currentProject.fileItems);

                // Get default materials
                const { materials, businessId } = await AssetsConfig.getMaterials(
                    null,
                    currentProject?.projectType || 'kitchen'
                );
                setBusinessId(businessId);
                // console.log('business_Id:', businessId);

                // setMaterialsData(materials);
                const typeLegacy = await AssetsConfig.getTypeLegacy(businessId);

                // Process initial data
                const { grouped, categories, initialSelected, initialDropdownStates, initialCategoryFilters } =
                    await processInitialData(materials);

                const mapLegacyMaterialType = materialType => {
                    return typeLegacy[materialType] || materialType;
                };
                // console.log('grouped:', grouped);
                // Set initial states
                setGroupedData(grouped);
                setIsDropdownOpen(initialDropdownStates);
                setCategoryFilters(initialCategoryFilters);
                setAvailableCategories(categories);

                if (currentProject.materials) {
                    let savedMaterials = currentProject.materials;
                    if (typeof savedMaterials === 'string') {
                        savedMaterials = JSON.parse(savedMaterials);
                    }

                    // Handle custom layers first - download and create URLs
                    const customMaterials = savedMaterials.filter(
                        material => material.item?.s3Key && material.type.startsWith('Custom')
                    );

                    const customUrlPromises = customMaterials.map(async material => {
                        try {
                            const response = await ProjectService.downloadFileFromS3(material.item.s3Key);
                            const url = URL.createObjectURL(response);

                            // Preload the custom image
                            await new Promise((resolve, reject) => {
                                const img = new Image();
                                img.onload = resolve;
                                img.onerror = reject;
                                img.src = url;
                            });

                            return { s3Key: material.item.s3Key, url };
                        } catch (error) {
                            console.error('Failed to load custom image:', error);
                            return null;
                        }
                    });

                    // Load regular material images
                    const regularMaterials = savedMaterials.filter(material => !material.type.startsWith('Custom'));

                    const regularImagePromises = regularMaterials.map(
                        material =>
                            new Promise(resolve => {
                                const img = new Image();
                                img.onload = resolve;
                                img.onerror = resolve;
                                img.src = AssetsConfig.getImageUrl(material.item, businessId);
                            })
                    );

                    // Wait for all images to load
                    const [customUrls] = await Promise.all([
                        Promise.all(customUrlPromises),
                        Promise.all(regularImagePromises),
                    ]);

                    // Store the custom URLs
                    const newRefreshedUrls = {};
                    customUrls.forEach(result => {
                        if (result) {
                            newRefreshedUrls[result.s3Key] = result.url;
                        }
                    });
                    setRefreshedUrls(newRefreshedUrls);

                    // Setup selected items and layers from all materials
                    const loadedSelectedItems = {};
                    savedMaterials.forEach(material => {
                        const mappedType = mapLegacyMaterialType(material.type);
                        loadedSelectedItems[mappedType] = material.item;
                    });
                    setSelectedItems(loadedSelectedItems);

                    const loadedLayers = savedMaterials.map((material, index) => ({
                        id: material.type,
                        type: material.type,
                        item: material.item,
                        x: parseFloat(material.position.x),
                        y: parseFloat(material.position.y),
                        zIndex: material.zIndex !== undefined ? parseInt(material.zIndex) : index,
                        scale: material.scale ? parseFloat(material.scale) : null,
                        visible: material.visible !== undefined ? material.visible : true,
                    }));

                    // loadedLayers.sort((a, b) => a.zIndex - b.zIndex);
                    setLayers(loadedLayers);

                    // Add custom layers to grouped data
                    if (customMaterials.length > 0) {
                        setGroupedData(prev => ({
                            ...prev,
                            Custom: customMaterials.map(m => m.item),
                        }));
                    }
                } else {
                    // Handle case when no saved materials exist
                    setSelectedItems(initialSelected);
                    const initialItems = Object.entries(initialSelected).filter(([_, item]) => item);
                    const positions = calculateGridPositions(
                        initialItems.length,
                        canvasWidth,
                        canvasHeight,
                        thumbnailSize
                    );

                    const initialLayers = initialItems.map(([type, item], index) => ({
                        id: type,
                        type,
                        item,
                        x: positions[index].x,
                        y: positions[index].y,
                        zIndex: index,
                        scale: null,
                        visible: true,
                    }));
                    setLayers(initialLayers);
                }
            } catch (error) {
                console.error('Error initializing moodboard:', error);
            } finally {
                setLoading(false);
            }
        };

        setLoading(true);
        initializeData();
    }, [projectId]);

    useEffect(() => {
        const saveMaterials = async () => {
            try {
                const materialsToSave = layers.map(layer => ({
                    type: layer.type,
                    item: selectedItems[layer.type],
                    position: {
                        x: layer.x.toString(),
                        y: layer.y.toString(),
                    },
                    zIndex: layer.zIndex.toString(),
                    scale: layer.scale ? layer.scale.toString() : null,
                    visible: layer.visible,
                }));

                await ProjectService.updateProject(projectId, {
                    materials: materialsToSave,
                });
            } catch (error) {
                console.error('Failed to save materials:', error);
            }
        };
        // Clear any existing timeout when dragging starts
        if (isDragging) {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
                saveTimeoutRef.current = null;
            }
            return;
        }

        // Set a new timeout when dragging stops
        if (Object.keys(selectedItems).length > 0) {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
            saveTimeoutRef.current = setTimeout(() => {
                saveMaterials();
            }, 1500);
        }

        // Cleanup timeout on unmount
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [selectedItems, layers, projectId, isDragging]);

    const handleCustomLayerCreated = async (customItem, fileItem) => {
        try {
            // First, download and create URL for the custom image
            const response = await ProjectService.downloadFileFromS3(customItem.s3Key);
            const url = URL.createObjectURL(response);

            // Preload the image before adding the layer
            await new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = resolve;
                img.onerror = reject;
                img.src = url;
            });

            // Update refreshedUrls with the new URL
            setRefreshedUrls(prev => ({
                ...prev,
                [customItem.s3Key]: url,
            }));

            // Create unique type for the layer
            const timestamp = Date.now();
            const uniqueType = `Custom_${customItem.Item_name}_${timestamp}`;

            // Create new layer - keep the same structure but adjust the position calculation if needed
            const newLayer = {
                id: uniqueType,
                type: uniqueType,
                item: customItem,
                x: canvasOffset + Math.random() * (canvasWidth - thumbnailSize - canvasOffset),
                y: canvasOffset + Math.random() * (canvasHeight - thumbnailSize - canvasOffset),
                zIndex: layers.length + 1, // Higher zIndex to appear on top
                scale: null,
                visible: true,
            };

            // Update layers state - add the new layer at the beginning (top) of the array
            // and update zIndices to reflect the new order
            setLayers(prev => {
                // Add new layer at the beginning
                const updatedLayers = [newLayer, ...prev];

                // Update zIndex values to ensure the new layer is on top
                return updatedLayers.map((layer, index) => ({
                    ...layer,
                    zIndex: updatedLayers.length - index - 1,
                }));
            });

            // Update selected items
            setSelectedItems(prev => ({
                ...prev,
                [uniqueType]: customItem,
            }));

            // Update grouped data - also place custom items at the beginning of the Custom array
            setGroupedData(prev => ({
                ...prev,
                Custom: prev.Custom ? [customItem, ...prev.Custom] : [customItem],
            }));

            // Update file items
            const fileItemsToSave = [...fileItems, fileItem];
            setFileItems(fileItemsToSave);

            // Prep the materials to save - ensure the new layer is at the top
            const newLayerForSave = {
                type: uniqueType,
                item: customItem,
                position: {
                    x: newLayer.x.toString(),
                    y: newLayer.y.toString(),
                },
                zIndex: (layers.length + 1).toString(), // Highest zIndex
                scale: newLayer.scale ? newLayer.scale.toString() : null,
                visible: newLayer.visible,
            };

            const existingLayersForSave = layers.map((layer, index) => ({
                type: layer.type,
                item: selectedItems[layer.type],
                position: {
                    x: layer.x.toString(),
                    y: layer.y.toString(),
                },
                zIndex: index.toString(), // Update existing layers' zIndices
                scale: layer.scale ? layer.scale.toString() : null,
                visible: layer.visible,
            }));

            // Save with the new layer at the beginning
            await ProjectService.updateProject(projectId, {
                materials: [newLayerForSave, ...existingLayersForSave],
                fileItems: fileItemsToSave,
            });

            // Set the newly created layer as selected
            setSelectedLayerId(uniqueType);
        } catch (error) {
            console.error('Error creating custom layer:', error);
        }
    };

    const handleItemSelect = (type, item) => {
        // Skip if the material is unavailable
        if (item.isUnavailable) {
            return;
        }

        // Update selected items with the full unique type
        setSelectedItems(prev => ({
            ...prev,
            [type]: item,
        }));

        // Update layers with the new material
        setLayers(prev => {
            return prev.map(layer => {
                if (layer.type === type) {
                    // Match the exact type
                    return {
                        ...layer,
                        item,
                        x: layer.x,
                        y: layer.y,
                        scale: layer.scale,
                    };
                }
                return layer;
            });
        });

        // Close the dropdown
        setIsDropdownOpen(prev => ({
            ...prev,
            [type]: false,
        }));
    };

    const getLayerImageUrl = item => {
        if (!item) return FALLBACK_IMAGE;

        // For unavailable materials
        if (item.isUnavailable) {
            return FALLBACK_IMAGE;
        }

        // For custom layers with s3Key
        if (item.s3Key && refreshedUrls[item.s3Key]) {
            return refreshedUrls[item.s3Key];
        }
        if (item.s3Key) {
            return FALLBACK_IMAGE;
        }

        // For regular items
        try {
            const url = AssetsConfig.getImageUrl(item, businessId);
            return url ? url : FALLBACK_IMAGE;
        } catch (error) {
            console.error('Error getting image URL:', error);
            return 'FALLBACK_IMAGE';
        }
    };

    useEffect(() => {
        const loadImagesAndDraw = async () => {
            const imageLoadPromises = Object.entries(selectedItems)
                .filter(([_, item]) => item)
                .map(async ([type, item]) => {
                    return new Promise(resolve => {
                        const img = new Image();
                        img.src = getLayerImageUrl(item);
                        img.onload = () => {
                            layerImagesRef.current[type] = img;
                            resolve();
                        };
                        img.onerror = resolve;
                    });
                });

            await Promise.all(imageLoadPromises);
            drawCanvas();
        };

        loadImagesAndDraw();
    }, [selectedItems, layers, selectedLayerId]);

    useEffect(() => {
        return () => {
            Object.values(refreshedUrls).forEach(url => {
                if (url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, [refreshedUrls]);

    useEffect(() => {
        drawCanvas();
    }, [layers]);

    const handleComplete = async () => {
        try {
            // First save all current materials state
            const materialsToSave = layers.map(layer => ({
                type: layer.type,
                item: selectedItems[layer.type],
                position: {
                    x: layer.x.toString(),
                    y: layer.y.toString(),
                },
                zIndex: layer.zIndex.toString(),
                scale: layer.scale ? layer.scale.toString() : null,
                visible: layer.visible,
            }));

            // Wait for the materials to be saved first
            await ProjectService.updateProject(projectId, {
                materials: materialsToSave,
            });

            // Only after materials are saved, add the completed flow
            await ProjectService.addCompletedFlow(projectId, 'colors');

            // Then navigate
            navigate(`/project/${projectId}`);
        } catch (error) {
            console.error('Error completing section:', error);
        }
    };

    useEffect(() => {
        const loadImagesAndDraw = async () => {
            const imageLoadPromises = Object.entries(selectedItems)
                .filter(([_, item]) => item)
                .map(([type, item]) => {
                    return new Promise(resolve => {
                        const img = new Image();
                        img.src = AssetsConfig.getImageUrl(item, businessId);
                        img.onload = () => {
                            layerImagesRef.current[type] = img;
                            resolve();
                        };
                        img.onerror = resolve;
                    });
                });

            await Promise.all(imageLoadPromises);
            drawCanvas();
        };

        loadImagesAndDraw();
    }, []);

    useEffect(() => {
        setLayers(prev => {
            const updatedLayers = prev.map(layer => {
                const selectedItem = selectedItems[layer.type];
                return selectedItem ? { ...layer, item: selectedItem } : layer;
            });

            const newLayers = [];
            Object.entries(selectedItems).forEach(([type, item]) => {
                if (item && !updatedLayers.find(layer => layer.type === type)) {
                    newLayers.push({
                        id: type,
                        type,
                        item,
                        x: 0,
                        y: 0,
                        zIndex: updatedLayers.length + newLayers.length,
                        scale: null,
                    });
                }
            });

            if (newLayers.length > 0) {
                const allLayers = [...updatedLayers, ...newLayers];
                const positions = calculateGridPositions(allLayers.length, canvasWidth, canvasHeight, thumbnailSize);

                return allLayers.map((layer, index) => ({
                    ...layer,
                    x: positions[index].x,
                    y: positions[index].y,
                }));
            }

            return updatedLayers;
        });
    }, [selectedItems]);

    useEffect(() => {
        const handleClickOutside = event => {
            const dropdowns = document.querySelectorAll('.material-dropdown');
            const clickedOutside = ![...dropdowns].some(dropdown => dropdown.contains(event.target));

            if (clickedOutside) {
                setIsDropdownOpen(prev =>
                    Object.keys(prev).reduce(
                        (acc, key) => ({
                            ...acc,
                            [key]: false,
                        }),
                        {}
                    )
                );
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const calculateGridPositions = (numItems, canvasWidth, canvasHeight, itemSize) => {
        const maxItemsPerRow = Math.ceil(Math.sqrt(numItems));
        const rows = Math.ceil(numItems / maxItemsPerRow);
        const cols = Math.ceil(numItems / rows);

        const horizontalGap = (canvasWidth - cols * itemSize) / (cols + 1);
        const verticalGap = (canvasHeight - rows * itemSize) / (rows + 1);

        const positions = [];
        let itemIndex = 0;

        for (let row = 0; row < rows; row++) {
            const itemsInThisRow = row === rows - 1 ? numItems - itemIndex : cols;

            for (let col = 0; col < itemsInThisRow; col++) {
                positions.push({
                    x: horizontalGap + col * (itemSize + horizontalGap),
                    y: verticalGap + row * (itemSize + verticalGap),
                });
                itemIndex++;
            }
        }

        return positions;
    };
    const createUnavailableMaterial = type => ({
        Item_name: `${type} (Unavailable)`,
        Type: type,
        Categories: [],
        isUnavailable: true,
    });

    const processInitialData = async data => {
        const grouped = {};
        const categories = {};
        // console.log('materialsData:', data);

        data.forEach(item => {
            const type = item.Type;
            if (!grouped[type]) {
                grouped[type] = [];
            }
            grouped[type].push(item);

            if (!categories[type]) {
                categories[type] = new Set();
            }

            const itemCategories = Array.isArray(item.Categories) ? item.Categories : [item.Categories];
            itemCategories.forEach(category => {
                if (category) categories[type].add(category);
            });
        });

        // Add unavailable materials to empty groups
        Object.keys(grouped).forEach(type => {
            if (!grouped[type] || grouped[type].length === 0) {
                grouped[type] = [createUnavailableMaterial(type)];
            }
        });

        const finalCategories = Object.entries(categories).reduce((acc, [type, categorySet]) => {
            if (categorySet.size > 1) {
                acc[type] = Array.from(categorySet).sort();
            }
            return acc;
        }, {});

        const initialSelected = Object.keys(grouped).reduce(
            (acc, type) => ({
                ...acc,
                [type]: grouped[type][0] || createUnavailableMaterial(type),
            }),
            {}
        );

        const initialDropdownStates = Object.keys(grouped).reduce(
            (acc, type) => ({
                ...acc,
                [type]: false,
            }),
            {}
        );

        const initialCategoryFilters = Object.keys(grouped).reduce(
            (acc, type) => ({
                ...acc,
                [type]: '',
            }),
            {}
        );

        return {
            grouped,
            categories: finalCategories,
            initialSelected,
            initialDropdownStates,
            initialCategoryFilters,
        };
    };

    const drawCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
    
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
    
        const sortedLayers = [...layers]
            .filter(layer => layer.visible) // Only draw visible layers
            .sort((a, b) => a.zIndex - b.zIndex);
    
        sortedLayers.forEach(layer => {
            const img = layerImagesRef.current[layer.type];
            if (img) {
                const baseScale = Math.min(thumbnailSize / img.width, thumbnailSize / img.height);
                const currentScale = layer.scale || baseScale;
                const width = img.width * currentScale;
                const height = img.height * currentScale;
    
                ctx.drawImage(img, layer.x, layer.y, width, height);

                // Text shadow for better visibility
                // Text configuration
                ctx.font = 'bold 13px arial';
                const text = isCustomLayer(layer.type) ? layer.item.Item_name : layer.type;
                const x = layer.x + 8;
                const y = layer.y + height - 8;

                // Layer 1 - Outermost glow
                ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
                ctx.shadowBlur = 8;
                ctx.fillStyle = 'white';
                ctx.fillText(text, x, y);

                // Layer 2 - Medium glow
                ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
                ctx.shadowBlur = 3;
                ctx.fillText(text, x, y);

                // Layer 3 - Inner glow
                ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
                ctx.shadowBlur = 2;
                ctx.fillText(text, x, y);

                // Layer 4 - Sharp inner glow
                ctx.shadowColor = 'rgba(0, 0, 0, 0.9)';
                ctx.shadowBlur = 2;
                ctx.fillText(text, x, y);

                // Final layer - Clear text
                ctx.shadowColor = 'transparent';
                ctx.shadowBlur = 0;
                ctx.fillStyle = 'white';
                ctx.fillText(text, x, y);

                // Reset all context properties
                ctx.shadowColor = 'transparent';
                ctx.shadowBlur = 0;
                ctx.shadowOffsetX = 0;
                ctx.shadowOffsetY = 0;

                if (layer.id === selectedLayerId) {
                    ctx.strokeStyle = '#3b82f6';
                    ctx.lineWidth = 2;
                    ctx.setLineDash([5, 3]);
                    ctx.strokeRect(layer.x - 2, layer.y - 2, width + 4, height + 4);
                    ctx.setLineDash([]);
    
                    // Draw resize handle
                    const handleSize = 15; // Slightly larger to accommodate the circle
                    const handleX = layer.x + width - handleSize / 2;
                    const handleY = layer.y + height - handleSize / 2;
    
                    // Draw white circle
                    ctx.beginPath();
                    ctx.arc(handleX + handleSize / 2, handleY + handleSize / 2, handleSize / 2, 0, Math.PI * 2);
                    ctx.fillStyle = isHandleHovered ? '#3b82f6' : 'white';
                    ctx.fill();
                    ctx.strokeStyle = '#3b82f6';
                    ctx.lineWidth = 1;
                    ctx.stroke();
                }
            }
        });
    };

    const toggleLayerVisibility = layerId => {
        setLayers(prev =>
            prev.map(layer => {
                if (layer.id === layerId) {
                    return {
                        ...layer,
                        visible: !layer.visible,
                    };
                }
                return layer;
            })
        );
    };

    const getCanvasScale = canvas => {
        const rect = canvas.getBoundingClientRect();
        return {
            scaleX: canvas.width / rect.width,
            scaleY: canvas.height / rect.height,
        };
    };

    const handleCanvasMouseDown = e => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const { scaleX, scaleY } = getCanvasScale(canvas);

        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;

        const x = (clientX - rect.left) * scaleX;
        const y = (clientY - rect.top) * scaleY;

        // Check if clicking resize handle of selected layer
        if (selectedLayerId) {
            const selectedLayer = layers.find(l => l.id === selectedLayerId);
            if (selectedLayer) {
                const img = layerImagesRef.current[selectedLayer.type];
                if (img) {
                    const baseScale = Math.min(thumbnailSize / img.width, thumbnailSize / img.height);
                    const currentScale = selectedLayer.scale || baseScale;
                    const width = img.width * currentScale;
                    const height = img.height * currentScale;

                    // Check if click is within resize handle area
                    const handleSize = 16;
                    const handleX = selectedLayer.x + width - handleSize / 2;
                    const handleY = selectedLayer.y + height - handleSize / 2;

                    if (x >= handleX && x <= handleX + handleSize && y >= handleY && y <= handleY + handleSize) {
                        setIsResizing(true);
                        setInitialSize({ width, height });
                        setInitialMouse({ x, y });
                        setResizeStartScale(currentScale);
                        // Store the original zIndex and bring the layer to the top
                        setLayers(prev => {
                            const maxZIndex = Math.max(...prev.map(l => l.zIndex));
                            return prev.map(layer => 
                                layer.id === selectedLayerId 
                                    ? { ...layer, zIndex: maxZIndex + 1, originalZIndex: layer.zIndex }
                                    : layer
                            );
                        });
                        return;
                    }
                }
            }
        }

        // Existing layer selection logic
        const clickedLayer = [...layers]
            .sort((a, b) => b.zIndex - a.zIndex)
            .find(layer => {
                const img = layerImagesRef.current[layer.type];
                if (!img) return false;

                const baseScale = Math.min(thumbnailSize / img.width, thumbnailSize / img.height);
                const currentScale = layer.scale || baseScale;
                const width = img.width * currentScale;
                const height = img.height * currentScale;

                return x >= layer.x && x <= layer.x + width && y >= layer.y && y <= layer.y + height;
            });

        if (clickedLayer) {
            setSelectedLayerId(clickedLayer.id);
            setDraggingLayer(clickedLayer);
            setDragOffset({
                x: x - clickedLayer.x,
                y: y - clickedLayer.y,
            });
        } else {
            setSelectedLayerId(null);
        }
    };

    const handleCanvasMouseMove = e => {
        const canvas = canvasRef.current;
        if (!canvas) return;
    
        const rect = canvas.getBoundingClientRect();
        const { scaleX, scaleY } = getCanvasScale(canvas);
    
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
        const x = (clientX - rect.left) * scaleX;
        const y = (clientY - rect.top) * scaleY;
    
        let isOverHandle = false;
        if (selectedLayerId) {
            const selectedLayer = layers.find(l => l.id === selectedLayerId);
            if (selectedLayer) {
                const img = layerImagesRef.current[selectedLayer.type];
                if (img) {
                    const baseScale = Math.min(thumbnailSize / img.width, thumbnailSize / img.height);
                    const currentScale = selectedLayer.scale || baseScale;
                    const width = img.width * currentScale;
                    const height = img.height * currentScale;
    
                    const handleSize = 20;
                    const handleX = selectedLayer.x + width - handleSize / 2;
                    const handleY = selectedLayer.y + height - handleSize / 2;
    
                    isOverHandle = x >= handleX && x <= handleX + handleSize && y >= handleY && y <= handleY + handleSize;
                }
            }
        }
        setIsHandleHovered(isOverHandle);
        canvas.style.cursor = isOverHandle ? 'nwse-resize' : 'default';
    
        if (isResizing && selectedLayerId) {
            const deltaX = x - initialMouse.x;
            const deltaY = y - initialMouse.y;

            const scaleDelta = Math.max(deltaX / initialSize.width, deltaY / initialSize.height);
            
            setLayers(prev =>
                prev.map(layer => {
                    if (layer.id === selectedLayerId) {
                        const img = layerImagesRef.current[layer.type];
                        if (!img) return layer;

                        // const baseScale = Math.min(thumbnailSize / img.width, thumbnailSize / img.height);
                        const newScale = resizeStartScale * (1 + scaleDelta);
                        
                        // Calculate new dimensions
                        const newWidth = img.width * newScale;
                        const newHeight = img.height * newScale;
                        
                        // Check if new dimensions would exceed canvas size (with 10px padding)
                        if (newWidth > canvasWidth - 40 || newHeight > canvasHeight - 40) {
                            // Calculate maximum allowed scale with 10px padding
                            const maxScaleX = (canvasWidth - 40) / img.width;
                            const maxScaleY = (canvasHeight - 40) / img.height;
                            const maxScale = Math.min(maxScaleX, maxScaleY);
                            
                            return {
                                ...layer,
                                scale: maxScale,
                            };
                        }

                        return {
                            ...layer,
                            scale: newScale,
                        };
                    }
                    return layer;
                })
            );
            return;
        }
    
        if (draggingLayer) {
            setIsDragging(true);
            const img = layerImagesRef.current[draggingLayer.type];
            if (img) {
                const baseScale = Math.min(thumbnailSize / img.width, thumbnailSize / img.height);
                const currentScale = draggingLayer.scale || baseScale;
                const width = img.width * currentScale;
                const height = img.height * currentScale;
    
                // Calculate new position
                let newX = x - dragOffset.x;
                let newY = y - dragOffset.y;
    
                // Constrain to canvas boundaries
                newX = Math.max(0, Math.min(canvasWidth - width, newX));
                newY = Math.max(0, Math.min(canvasHeight - height, newY));
    
                setLayers(prev =>
                    prev.map(layer => (layer.id === draggingLayer.id ? { ...layer, x: newX, y: newY } : layer))
                );
            }
        }
    };

    const handleCanvasMouseUp = () => {
        if (isResizing && selectedLayerId) {
            // Restore the original zIndex and remove the temporary originalZIndex property
            setLayers(prev => {
                const originalLayer = prev.find(l => l.id === selectedLayerId);
                if (!originalLayer) return prev;
                return prev.map(layer => 
                    layer.id === selectedLayerId 
                        ? { ...layer, zIndex: layer.originalZIndex }
                        : layer
                ).map(({ originalZIndex, ...rest }) => rest); // Remove the temporary property
            });
        }
        setIsDragging(false);
        setDraggingLayer(null);
        setIsResizing(false);
    };

    const removeLayer = async layer => {
        // Delete file from S3 if it's a custom layer with s3Key
        if (layer.item?.s3Key) {
            try {
                await ProjectService.deleteFileFromS3(layer.item.s3Key);

                // Revoke the URL if it exists
                if (refreshedUrls[layer.item.s3Key]) {
                    URL.revokeObjectURL(refreshedUrls[layer.item.s3Key]);
                    setRefreshedUrls(prev => {
                        const newUrls = { ...prev };
                        delete newUrls[layer.item.s3Key];
                        return newUrls;
                    });
                }
                const updatedFileItems = fileItems.filter(item => item.s3Key !== layer.item.s3Key);
                setFileItems(updatedFileItems);
                await ProjectService.updateProject(projectId, {
                    fileItems: updatedFileItems,
                });
            } catch (error) {
                console.error('Failed to delete file from S3:', error);
            }
        }

        // Remove from layers
        setLayers(prev => prev.filter(l => l.id !== layer.id));

        // Remove from selectedItems
        setSelectedItems(prev => {
            const newItems = { ...prev };
            delete newItems[layer.type];
            return newItems;
        });

        // Clear from grouped data if it's a custom layer
        if (isCustomLayer(layer.type)) {
            setGroupedData(prev => ({
                ...prev,
                Custom: Array.isArray(prev.Custom) ? prev.Custom.filter(item => item.s3Key !== layer.item?.s3Key) : [],
            }));
        }
    };

    // const removeFile = async (id) =>
    //     {
    //         try {
    //             const fileItem = fileItems.find(item => item.id === id);

    //             if (fileItem?.s3Key) {
    //                 await ProjectService.deleteFileFromS3(fileItem.s3Key);
    //             }

    //             if (localPreviews[id]?.url) {
    //                 URL.revokeObjectURL(localPreviews[id].url);
    //                 setLocalPreviews(prev => {
    //                     const updated = { ...prev };
    //                     delete updated[id];
    //                     return updated;
    //                 });
    //             }

    //             setFileItems(prev => prev.filter(item => item.id !== id));
    //             setUnsavedChanges(true);
    //         } catch (error) {
    //             console.error('Error removing file:', error);
    //         }
    //     };

    const handleDuplicateLayer = (e, layer) => {
        e.stopPropagation();
        setLayerToDuplicate(layer);
        setIsDuplicateDialogOpen(true);
    };

    // Updated handleDuplicateConfirm function to fix the "undefined_" prefix issue
    const handleDuplicateConfirm = (newName, originalType) => {
        if (!layerToDuplicate) return;

        // Create a new unique type identifier using the base type and provided name
        const uniqueType = `${newName}`;
        const duplicateId = `${layerToDuplicate.id}_${newName}`;

        // Create the duplicated layer preserving the original type for material selection
        const duplicatedLayer = {
            ...layerToDuplicate,
            id: duplicateId,
            type: uniqueType,
            item: {
                ...layerToDuplicate.item, // Preserve all item properties
            },
            x: layerToDuplicate.x + 20,
            y: layerToDuplicate.y + 20,
            zIndex: layers.length,
        };

        // Update layers state with the new layer at the top of the list
        setLayers(prev => {
            // Add the duplicated layer at the beginning of the array
            const updatedLayers = [duplicatedLayer, ...prev];

            // Update zIndex values to reflect the new order
            return updatedLayers.map((layer, index) => ({
                ...layer,
                zIndex: updatedLayers.length - index - 1,
            }));
        });

        // Update selected items to include the duplicated layer's material
        setSelectedItems(prev => ({
            ...prev,
            [uniqueType]: layerToDuplicate.item,
        }));

        // Save the updated state to the project
        const newLayers = [duplicatedLayer, ...layers];
        const materialsToSave = newLayers.map((l, index) => ({
            type: l.type,
            item: l.type === uniqueType ? layerToDuplicate.item : selectedItems[l.type],
            position: {
                x: l.x.toString(),
                y: l.y.toString(),
            },
            zIndex: (newLayers.length - index - 1).toString(),
            scale: l.scale ? l.scale.toString() : null,
            visible: l.visible,
        }));

        ProjectService.updateProject(projectId, {
            materials: materialsToSave,
        }).catch(error => {
            console.error('Error saving duplicated Element:', error);
        });

        // Clear the layer to duplicate
        setLayerToDuplicate(null);
    };

    const handleCategoryFilter = (type, category) => {
        // Extract the base type without any suffix
        const baseType = type.includes('_') ? type.split('_')[0] : type;

        setCategoryFilters(prev => ({
            ...prev,
            [baseType]: category, // Store filter by base type
            [type]: category, // Also store by full type for consistency
        }));
    };

    const getFilteredItems = (type, items) => {
        try {
            if (!type || !items) {
                console.warn(`Invalid input for getFilteredItems: type=${type}, items=`, items);
                return [createUnavailableMaterial(type)];
            }

            // Get category filter using either the exact type or base type
            const selectedCategory =
                categoryFilters[type] || (type.includes('_') ? categoryFilters[type.split('_')[0]] : null);

            if (!selectedCategory) return items;

            const filteredItems = items.filter(item => {
                if (!item) return false;
                if (item.isUnavailable) return true;

                const itemCategories = item.Categories
                    ? Array.isArray(item.Categories)
                        ? item.Categories
                        : [item.Categories]
                    : [];

                return itemCategories.includes(selectedCategory);
            });

            return filteredItems.length > 0 ? filteredItems : [createUnavailableMaterial(type)];
        } catch (error) {
            console.error('Error in getFilteredItems:', error);
            return [createUnavailableMaterial(type)];
        }
    };

    // Safe render function for materials list
    const renderMaterialsList = (type, items, onSelectMaterial) => {
        try {
            // Get base type for filtering - either from the item's Type or from layer type
            const baseType = type.includes('_') ? type.split('_')[0] : type;
            const filteredItems = getFilteredItems(baseType, items) || [];

            return filteredItems
                .map(item => {
                    if (!item) return null;

                    const itemName = item.Item_name || item.name || 'Unnamed Material';
                    const itemKey = itemName || Math.random().toString();

                    return (
                        <div
                            key={itemKey}
                            className={`cursor-pointer hover:bg-gray-50 ${
                                selectedItems[type]?.Item_name === itemName ? 'bg-blue-50' : ''
                            }`}
                            onClick={e => onSelectMaterial(e, type, item)}
                        >
                            <div className="flex items-center p-2 gap-4">
                                <div className="relative w-16 h-16 flex-shrink-0">
                                    <img
                                        src={AssetsConfig.getImageUrl(item, businessId)}
                                        alt={itemName || 'Material'}
                                        className="absolute inset-0 w-full h-full object-contain"
                                        onError={e => {
                                            e.target.onerror = null;
                                            e.target.src =
                                                'https://gni-public-bucket.s3.us-east-1.amazonaws.com/assets/gni/notAvailible.jpg';
                                        }}
                                    />
                                </div>
                                <p className="flex-1 text-sm">{itemName.replace(/_/g, ' ')}</p>
                            </div>
                        </div>
                    );
                })
                .filter(Boolean);
        } catch (error) {
            console.error('Error rendering materials list:', error);
            return <div className="p-2 text-red-500">Error loading materials</div>;
        }
    };

    const handleBack = () => {
        navigate(`/project/${projectId}/style`);
    };

    const handleLayerReorder = (draggedId, droppedId, dropPosition) => {
        if (draggedId === droppedId) return;

        setLayers(prev => {
            const currentLayers = [...prev];
            const draggedLayer = currentLayers.find(l => l.id === draggedId);
            if (!draggedLayer) return prev;
            const droppedLayer = currentLayers.find(l => l.id === droppedId);
            if (!droppedLayer) return prev;
            const withoutDragged = currentLayers.filter(l => l.id !== draggedId);
            const targetIndex = withoutDragged.findIndex(l => l.id === droppedId);
            const newPosition = dropPosition === 'after' ? targetIndex + 1 : targetIndex;
            withoutDragged.splice(newPosition, 0, draggedLayer);
            return withoutDragged.map((layer, index) => ({
                ...layer,
                zIndex: withoutDragged.length - index - 1,
            }));
        });
    };

    const renderLayerControls = () => {
        const toggleLayerPanelDropdown = (e, type) => {
            e.stopPropagation();
            setLayerPanelDropdowns(prev => {
                const newState = {};
                Object.keys(prev).forEach(key => {
                    newState[key] = false;
                });
                newState[type] = !prev[type];
                return newState;
            });
        };

        const handleLayerMaterialSelect = (e, type, item) => {
            e.stopPropagation();
            handleItemSelect(type, item);
            setLayerPanelDropdowns(prev => ({
                ...prev,
                [type]: false,
            }));
        };

        return (
            <div className="bg-white p-3 rounded-lg">
                <button
                    onClick={() => setIsLayerControlsOpen(!isLayerControlsOpen)}
                    className="w-full flex items-center justify-between mb-2"
                >
                    <h3 className="text-lg font-semibold">Material selection</h3>
                    <ChevronDown
                        className={`transform transition-transform ${isLayerControlsOpen ? 'rotate-180' : ''}`}
                        size={20}
                    />
                </button>

                {isLayerControlsOpen && (
                    <div className="space-y-2">
                        <CustomLayerDialog projectId={projectId} onLayerCreated={handleCustomLayerCreated} />
                        {[...layers].map(layer => (
                            <div key={layer.id} className="relative">
                                {/* Drop zone above layer */}
                                {draggedLayer && draggedLayer.id !== layer.id && (
                                    <div
                                        className="absolute w-full h-3 -top-1.5 z-10"
                                        onDragOver={e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setDragOverInfo({ id: layer.id, position: 'before' });
                                        }}
                                        onDragLeave={e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setDragOverInfo(null);
                                        }}
                                        onDrop={e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (draggedLayer) {
                                                handleLayerReorder(draggedLayer.id, layer.id, 'before');
                                            }
                                            setDragOverInfo(null);
                                            setDraggedLayer(null);
                                        }}
                                    >
                                        {dragOverInfo?.id === layer.id && dragOverInfo?.position === 'before' && (
                                            <div className="h-0.5 w-full bg-blue-500" />
                                        )}
                                    </div>
                                )}

                                {/* Layer item */}
                                <div
                                    className={`flex items-center bg-white rounded shadow-sm
                                        ${layer.id === selectedLayerId ? 'ring-2 ring-blue-500' : ''}
                                        ${!layer.visible ? 'opacity-50' : ''}
                                        ${draggedLayer?.id === layer.id ? 'opacity-50' : ''}
                                        transition-colors duration-200`}
                                    onClick={() => setSelectedLayerId(layer.id)}
                                >
                                    {/* Drag Handle */}
                                    <div
                                        className="p-2 cursor-grab hover:bg-gray-100 rounded-l flex items-center"
                                        draggable="true"
                                        onDragStart={e => {
                                            e.stopPropagation();
                                            setDraggedLayer(layer);
                                        }}
                                        onDragEnd={e => {
                                            e.stopPropagation();
                                            setDraggedLayer(null);
                                            setDragOverInfo(null);
                                        }}
                                    >
                                        <GripVertical className="text-gray-400 hover:text-gray-600" size={16} />
                                    </div>

                                    {/* Layer Content - Now clickable to toggle dropdown */}
                                    <div
                                        className="flex-1 flex items-center justify-between p-2 w-[65%] cursor-pointer"
                                        onClick={e => {
                                            e.stopPropagation();
                                            toggleLayerPanelDropdown(e, layer.type);
                                            setSelectedLayerId(layer.id);
                                        }}
                                    >
                                        <div className="flex items-center gap-2 w-[100%]">
                                            <div className="w-10 h-10 flex-shrink-0">
                                                {isCustomLayer(layer.type) ? (
                                                    <Custom className="w-full h-full text-gray-600" />
                                                ) : (
                                                    <img
                                                        src={AssetsConfig.getImageUrl(layer.item, businessId)}
                                                        alt={layer.item?.Item_name}
                                                        className="w-full h-full object-contain"
                                                        onError={e => {
                                                            e.target.onerror = null;
                                                            e.target.src =
                                                                'https://gni-public-bucket.s3.us-east-1.amazonaws.com/assets/gni/notAvailible.jpg';
                                                        }}
                                                    />
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-bold text-sm mb-1 truncate">
                                                    {layer.type.includes('_')
                                                        ? `${
                                                              isCustomLayer(layer.type)
                                                                  ? layer.item.Item_name
                                                                  : layer.type
                                                          }`
                                                        : layer.type}
                                                </p>
                                                <p className="text-sm text-gray-600 truncate">
                                                    {isCustomLayer(layer.type)
                                                        ? layer.item.Type
                                                        : layer.item?.Item_name?.replace(/_/g, ' ')}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        {isCustomLayer(layer.type) ? (
                                            <button
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    removeLayer(layer);
                                                }}
                                                className="p-1 hover:bg-gray-100 rounded text-red-500 hover:text-red-600"
                                                title="Remove custom layer"
                                            >
                                                <X size={16} />
                                            </button>
                                        ) : (
                                            <>
                                                <button
                                                    onClick={e => toggleLayerPanelDropdown(e, layer.type)}
                                                    className="p-1 hover:bg-gray-100 rounded"
                                                >
                                                    <ChevronDown
                                                        className={`transform transition-transform ${
                                                            layerPanelDropdowns[layer.type] ? 'rotate-180' : ''
                                                        }`}
                                                        size={16}
                                                    />
                                                </button>

                                                {/* Duplicate button */}
                                                <button
                                                    onClick={e => handleDuplicateLayer(e, layer)}
                                                    className="p-1 hover:bg-gray-100 rounded text-gray-600 hover:text-gray-800"
                                                    title="Duplicate layer"
                                                >
                                                    <CopyPlus size={16} />
                                                </button>

                                                <button
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        toggleLayerVisibility(layer.id);
                                                    }}
                                                    className={`p-1 hover:bg-gray-100 rounded transition-colors
                                                                    ${
                                                                        layer.visible
                                                                            ? 'text-blue-500 hover:text-blue-600'
                                                                            : 'text-gray-400 hover:text-gray-500'
                                                                    }`}
                                                    title={layer.visible ? 'Hide layer' : 'Show layer'}
                                                >
                                                    {layer.visible ? <Eye size={16} /> : <EyeOff size={16} />}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Material Selection Dropdown */}
                                {layerPanelDropdowns[layer.type] && (
                                    <div
                                        className="border border-blue-200 bg-white mb-2 rounded-b shadow-sm"
                                        onClick={e => e.stopPropagation()}
                                    >
                                        {/* Category Filter */}
                                        {availableCategories[layer.type.split('_')[0]] &&
                                            availableCategories[layer.type.split('_')[0]].length > 0 && (
                                                <div className="p-2 border-b">
                                                    <div className="flex items-center gap-2">
                                                        <Filter size={16} className="text-gray-400" />
                                                        <select
                                                            className="w-full p-1 text-sm border rounded"
                                                            value={categoryFilters[layer.type]}
                                                            onChange={e => {
                                                                e.stopPropagation();
                                                                handleCategoryFilter(layer.type, e.target.value);
                                                            }}
                                                        >
                                                            <option value="">All types</option>
                                                            {availableCategories[layer.type.split('_')[0]].map(
                                                                category => (
                                                                    <option key={category} value={category}>
                                                                        {category}
                                                                    </option>
                                                                )
                                                            )}
                                                        </select>
                                                    </div>
                                                </div>
                                            )}

                                        {/* Materials List */}
                                        <div className="max-h-64 overflow-y-auto">
                                            {renderMaterialsList(
                                                layer.type, // Use layer.type directly, not split
                                                groupedData[layer.item?.Type || layer.type.split('_')[0]], // Get materials from the correct type based on item.Type
                                                handleLayerMaterialSelect
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Drop zone below layer */}
                                {draggedLayer && draggedLayer.id !== layer.id && (
                                    <div
                                        className="absolute w-full h-3 -bottom-1.5 z-10"
                                        onDragOver={e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setDragOverInfo({ id: layer.id, position: 'after' });
                                        }}
                                        onDragLeave={e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setDragOverInfo(null);
                                        }}
                                        onDrop={e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (draggedLayer) {
                                                handleLayerReorder(draggedLayer.id, layer.id, 'after');
                                            }
                                            setDragOverInfo(null);
                                            setDraggedLayer(null);
                                        }}
                                    >
                                        {dragOverInfo?.id === layer.id && dragOverInfo?.position === 'after' && (
                                            <div className="h-0.5 w-full bg-blue-500" />
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen">
                <ProjectHeader showBackButton={true} />
                <main className="pt-20 px-4">
                    <div className="flex justify-center items-center h-64">
                        <div className="text-lg">Loading materials...</div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen">
            <ProjectHeader showBackButton={true} collectionData={collectionData} projectName={projectData?.name} />
            <main className="pt-20 px-4 pb-24">
                {/* Render navigation only if project is part of a collection */}
                {projectData?.collectionId && (
                    <CollectionNavigation
                        // collectionId={projectData.collectionId}
                        collectionData={collectionData}
                        isLoading={iscollectionLoading}
                        currentProjectId={projectId}
                    />
                )}
                <div className={`${projectData?.collectionId ? 'md:pl-16 lg:pl-16' : ''}`}>
                    <div className="mb-2 bg-white rounded-lg shadow-sm p-4 border border-gray-100 text-center">
                        <div className="flex items-center justify-between ml-4 md:block">
                            <h2 className="text-xl font-semibold ">Your Personalized Moodboard</h2>
                            <button
                                onClick={() => setShowInstructions(!showInstructions)}
                                className="md:hidden p-2 hover:bg-gray-100 rounded-full"
                            >
                                <Info
                                    className={`text-blue-500 transition-transform ${
                                        showInstructions ? 'rotate-180' : ''
                                    }`}
                                    size={20}
                                />
                            </button>
                        </div>
                        <div
                            className={`grid grid-cols-1 md:grid-cols-4 gap-4 transition-all duration-300 ease-in-out ${
                                showInstructions ? 'max-h-96' : 'max-h-0 md:max-h-96'
                            } overflow-hidden`}
                        >
                            <div className="flex items-start space-x-3 p-3 bg-white rounded-lg">
                                <div className="mt-1">
                                    <Hand className="text-blue-500" size={20} />
                                </div>
                                <div>
                                    <h3 className="font-semibold">Drag</h3>
                                    <p className="text-sm text-gray-600">Move elements freely on the canvas</p>
                                </div>
                            </div>
                            <div className="flex items-start space-x-3 p-3 bg-white rounded-lg">
                                <div className="mt-1">
                                    <MoveDiagonal2 className="text-blue-500" size={20} />
                                </div>
                                <div>
                                    <h3 className="font-semibold">Resize</h3>
                                    <p className="text-sm text-gray-600">Adjust size using corner handles</p>
                                </div>
                            </div>
                            <div className="flex items-start space-x-3 p-3 bg-white rounded-lg">
                                <div className="mt-1">
                                    <CopyPlus className="text-blue-500" size={20} />
                                </div>
                                <div>
                                    <h3 className="font-semibold">Duplicate</h3>
                                    <p className="text-sm text-gray-600">Create copies of any element</p>
                                </div>
                            </div>
                            <div className="flex items-start space-x-3 p-3 bg-white0 rounded-lg">
                                <div className="mt-1">
                                    <Plus className="text-blue-500" size={20} />
                                </div>
                                <div>
                                    <h3 className="font-semibold">Add Custom</h3>
                                    <p className="text-sm text-gray-600">Upload your own elements</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col lg:flex-row gap-4">
                        {/* Layer controls */}
                        <Card className="lg:w-2/5">
                            <CardContent className="p-0 lg:h-[min(calc(100vh-180px),calc(60vw))] lg:overflow-hidden flex flex-col">
                                <div className="flex-1 overflow-y-auto">
                                    <div className="flex-1 overflow-y-auto">{renderLayerControls()}</div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Canvas */}
                        <Card className="lg:w-3/5">
                            <CardContent className="p-1 flex items-center justify-center">
                                <div
                                    className="relative bg-gray-50 rounded-lg touch-none"
                                    style={{
                                        aspectRatio: '4/3',
                                        height: 'min(calc(100vh - 220px), calc(60vw))',
                                        maxHeight: 'calc(60vw )',
                                    }}
                                >
                                    <canvas
                                        ref={canvasRef}
                                        width={canvasWidth}
                                        height={canvasHeight}
                                        className="w-full h-full border rounded-lg touch-none"
                                        onMouseDown={handleCanvasMouseDown}
                                        onMouseMove={handleCanvasMouseMove}
                                        onMouseUp={handleCanvasMouseUp}
                                        onMouseLeave={handleCanvasMouseUp}
                                        onTouchStart={e => {
                                            e.preventDefault();
                                            handleCanvasMouseDown(e);
                                        }}
                                        onTouchMove={e => {
                                            e.preventDefault();
                                            handleCanvasMouseMove(e);
                                        }}
                                        onTouchEnd={e => {
                                            e.preventDefault();
                                            handleCanvasMouseUp(e);
                                        }}
                                        onTouchCancel={e => {
                                            e.preventDefault();
                                            handleCanvasMouseUp(e);
                                        }}
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </main>
            <DuplicateLayerDialog
                isOpen={isDuplicateDialogOpen}
                onClose={() => {
                    setIsDuplicateDialogOpen(false);
                    setLayerToDuplicate(null);
                }}
                onConfirm={handleDuplicateConfirm}
                defaultName={layerToDuplicate ? `${layerToDuplicate.type.split('_')[0]}` : ''}
            />

            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex justify-end items-center shadow-lg">
                {/* <Button variant="outline" onClick={handleBack}>
                    Back
                </Button> */}
                <Button onClick={handleComplete}>Complete This Section</Button>
            </div>
        </div>
    );
};
export default UserMoodboardScreen;
